<script setup lang="ts">
import { ref, watch, computed } from 'vue';
import { IonButton, IonModal, IonSpinner } from '@ionic/vue';
import { useI18n } from 'vue-i18n';

import TagCollections from '@/shared/components/TagCollections.vue';
import useUserProfile from '@/modules/profile/composables/useUserProfile';
import useToast from '@/shared/composables/useToast';
import { useQueryClient } from '@tanstack/vue-query';
import type { TenantConfig } from '@/shared/interfaces/tenant';

// Props & Emits
const props = defineProps({
	isOpen: { type: Boolean, required: true, default: false },
});
const emit = defineEmits(['onClose']);

// Init
const { locale, t } = useI18n();
const { postInterestsConfigured } = useUserProfile();
const { presentToast } = useToast();
const isOpenRef = ref(props.isOpen);
const isSubmitting = ref(false);
const selectedTagsCount = ref(0);
const tagCollectionsRef = ref<InstanceType<typeof TagCollections> | null>(null);
const hasSelectedTags = computed(() => selectedTagsCount.value > 0);

const queryClient = useQueryClient();
const tenantConfig = queryClient.getQueryCache().find(['tenantConfig']);
const interestsTranslations = (tenantConfig?.state.data as TenantConfig)?.interests_page_translations;
const interestsTitle = computed(() => interestsTranslations?.[locale.value.split('-')[0]]?.title || t('auth.sign_up_additional_page.step_seven.title'));
const interestsSubtitle = computed(() => interestsTranslations?.[locale.value.split('-')[0]]?.subtitle || t('auth.sign_up_additional_page.step_seven.subtitle'));

// Watch prop changes
watch(
	() => props.isOpen,
	(newValue) => {
		isOpenRef.value = newValue;
	},
);

// Save and close
const save = async () => {
	isSubmitting.value = true;
	try {
		await tagCollectionsRef.value?.saveTags();
		await postInterestsConfigured();
		emit('onClose');
	} catch (error) {
		await presentToast('top', t('error.general'), 5000, 'danger');
	} finally {
		isSubmitting.value = false;
	}
};
</script>

<template>
	<ion-modal :is-open="isOpenRef" :initial-breakpoint="0.6" :breakpoints="[0, 0.6, 0.85]" :backdrop-dismiss="false" class="interests-modal">
		<div class="interests-modal-inner">
			<!-- Header -->
			<div class="interests-modal-header">
				<div class="flex items-center justify-between">
					<h2 class="font-sourcesanspro-semibold text-m2 text-white">
						{{ interestsTitle }}
					</h2>
					<ion-button class="h-[30px] text-xs" shape="round" color="primary" size="small" :disabled="isSubmitting || !hasSelectedTags" @click="save">
						<ion-spinner v-if="isSubmitting" name="dots" class="w-4 h-4"></ion-spinner>
						<span v-else>{{ t('form.save') }}</span>
					</ion-button>
				</div>
				<p class="font-sourcesanspro-regular text-sm text-white/50 mt-1">
					{{ interestsSubtitle }}
				</p>
			</div>

			<!-- Body (scrollable) -->
			<div class="interests-modal-body">
				<TagCollections ref="tagCollectionsRef" :user-tags="[]" @update:tags-count="(count: number) => (selectedTagsCount = count)" />
			</div>
		</div>
	</ion-modal>
</template>

<style lang="scss" scoped>
.interests-modal {
	--background: #3b3846;
}

.interests-modal-inner {
	display: flex;
	flex-direction: column;
	height: 100%;
	background: #3b3846;
}

.interests-modal-header {
	flex-shrink: 0;
	padding: 24px 24px 16px;
}

.interests-modal-body {
	flex: 1;
	overflow-y: auto;
	padding: 0 24px;
}
</style>
